import { useCallback, useMemo } from 'react';
import {
  createFindSprintForTask,
  createFieldHandlers,
  createArchiveHandlers,
  createDuplicateHandler,
  createMissionHandler,
  createFocusTaskHandler,
} from './crossSprintActions';

/**
 * useCrossSprintActions
 * 
 * Handlers that can operate on tasks from the current sprint OR nested sprints.
 * Used by TodoListView Master mode which shows tasks from all nested sprints.
 * 
 * When a task belongs to the current sprint, delegates to the standard actions.
 * When a task belongs to a nested sprint, updates that sprint directly via onUpdateSprint.
 */
export const useCrossSprintActions = ({
  tasks,
  localSprint,
  allSprints,
  actions,
  modals,
  onUpdateSprint,
  onAddSprint,
  onSelectSprint,
  setCurrentView,
  flowchartRef,
}) => {
  // Helper to find which sprint a task belongs to
  const findSprintForTask = useCallback(
    createFindSprintForTask(tasks, localSprint, allSprints),
    [tasks, localSprint, allSprints]
  );

  // Field update handlers (rename, icon, priority, dueDate, status)
  const fieldHandlers = useMemo(() => createFieldHandlers({
    findSprintForTask,
    actions,
    modals,
    onUpdateSprint,
  }), [findSprintForTask, actions, modals, onUpdateSprint]);

  // Archive handlers (archive, unarchive, delete)
  const archiveHandlers = useMemo(() => createArchiveHandlers({
    findSprintForTask,
    actions,
    onUpdateSprint,
  }), [findSprintForTask, actions, onUpdateSprint]);

  // Duplicate handler
  const handleCrossSprintDuplicate = useCallback(
    createDuplicateHandler({
      findSprintForTask,
      allSprints,
      onAddSprint,
      onUpdateSprint,
    }),
    [findSprintForTask, allSprints, onAddSprint, onUpdateSprint]
  );

  // Add subtask as mission handler
  const handleAddSubtaskAsMission = useCallback(
    createMissionHandler({
      findSprintForTask,
      allSprints,
      onAddSprint,
      onUpdateSprint,
    }),
    [findSprintForTask, allSprints, onAddSprint, onUpdateSprint]
  );

  // Focus task in flowchart handler
  const handleCrossSprintFocusTask = useCallback(
    createFocusTaskHandler({
      findSprintForTask,
      setCurrentView,
      flowchartRef,
      onSelectSprint,
    }),
    [findSprintForTask, setCurrentView, flowchartRef, onSelectSprint]
  );

  return {
    findSprintForTask,
    // Field updates
    handleCrossSprintRename: fieldHandlers.handleCrossSprintRename,
    handleCrossSprintIconChange: fieldHandlers.handleCrossSprintIconChange,
    handleCrossSprintPriorityChange: fieldHandlers.handleCrossSprintPriorityChange,
    handleCrossSprintDifficultyChange: fieldHandlers.handleCrossSprintDifficultyChange,
    handleCrossSprintDueDateChange: fieldHandlers.handleCrossSprintDueDateChange,
    handleCrossSprintStatusChange: fieldHandlers.handleCrossSprintStatusChange,
    // Decision handlers
    handleCrossSprintDecisionAnswerChange: fieldHandlers.handleCrossSprintDecisionAnswerChange,
    handleCrossSprintDecisionOptionsChange: fieldHandlers.handleCrossSprintDecisionOptionsChange,
    // Archive operations
    handleCrossSprintArchive: archiveHandlers.handleCrossSprintArchive,
    handleCrossSprintUnarchive: archiveHandlers.handleCrossSprintUnarchive,
    handleCrossSprintDelete: archiveHandlers.handleCrossSprintDelete,
    // Complex operations
    handleAddSubtaskAsMission,
    handleCrossSprintFocusTask,
    handleCrossSprintDuplicate,
  };
};

export default useCrossSprintActions;
